import { useEffect } from "react";
import { WidgetSpotlightProvider } from "@/contexts/WidgetSpotlightContext";
import DemoWelcomeModal from "./DemoWelcomeModal";
import GuideRail from "./GuideRail";
import DemoChecklistLauncher from "./checklist/DemoChecklistLauncher";
import { demoTemplateFor } from "./templates";
import { demoWatchlistFor } from "./demoWatchlistFor";
import { setDemoWatchlist } from "@/pages/PortfolioBuilder/demoWatchlist";
import { visibleSteps } from "./visibleSteps";
import {
  getDemoGuideDevOverride,
  setDemoGuideDevOverride,
  useDemoGuideGate,
} from "./useDemoGuideGate";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { useStepDetection } from "./useStepDetection";

/**
 * Single mount point for all guided-demo UI, rendered once in App.jsx beside
 * OnboardingGate.
 *
 * POPOUT SAFETY RULE: as with src/onboarding/, nothing under src/demo-guide/
 * may be imported from src/widget-components/**. Popout windows portal
 * componentMap widgets and must never render this tree.
 *
 * The two gates cannot both fire: onboarding requires authenticated +
 * in_trial, this requires logged out. See __tests__/gateExclusivity.test.ts.
 */
export function DemoGuideGate({ children }: { children: React.ReactNode }) {
  const { phase, showChecklist } = useDemoGuideGate();
  const { state, begin, reset } = useDemoGuideStore((s) => ({
    state: s.state,
    begin: s.begin,
    reset: s.reset,
  }));

  const active = phase !== "ineligible";

  // Dev/testing: ?demo-guide=reset wipes local state and re-runs from the
  // picker. Unlike onboarding's equivalent this needs no server round-trip.
  useEffect(() => {
    if (getDemoGuideDevOverride() !== "reset") return;
    reset();
    setDemoGuideDevOverride("force");
  }, [reset]);

  // Record the arrival the moment the picker is due, so "saw it" and
  // "answered it" stay separable in the funnel.
  useEffect(() => {
    if (phase === "picker") begin();
  }, [phase, begin]);

  useStepDetection(active);

  /**
   * Re-install the desk's watchlist after a reload.
   *
   * pickDesk sets it, but that runs once and the selection outlives it in
   * localStorage — so on the next page load the filter panels would be empty
   * again while the run carried on asking them to apply a list.
   */
  const deskAssetClass = active ? state?.assetClass : undefined;
  useEffect(() => {
    if (!deskAssetClass) return;
    // Same resolver pickDesk uses. Resolving it independently here is how the
    // reload path came to install the desk template's list while the run was
    // asking them to apply the recorded plan's.
    const spec = demoWatchlistFor(deskAssetClass);
    if (spec) setDemoWatchlist(spec);
  }, [deskAssetClass]);

  // Which widget the rail is pointing at. Resolved here rather than in the
  // rail so the provider wraps the dashboard whether or not the rail renders.
  //
  // "filtered" resolves through the applied template, so the ring follows the
  // narrowed Headlines instance for whichever desk they picked without any
  // id being written down twice.
  let targetWidgetId: string | null = null;
  let targetControl: "highlight" | "filter" | "popout" | null = null;
  if (phase === "run" && state?.assetClass && !state.completedAt) {
    const step = visibleSteps(state.assetClass)[state.step];
    const template = demoTemplateFor(state.assetClass);
    targetControl = step?.control ?? null;
    if (step?.target === "filtered") {
      targetWidgetId = template?.filteredWireId ?? null;
    } else if (step?.target === "primary") {
      targetWidgetId = "NewsquawkHeadlines-primary";
    } else {
      targetWidgetId = step?.target ?? null;
    }
  }

  if (!active) return <>{children}</>;

  return (
    <WidgetSpotlightProvider
      targetWidgetId={targetWidgetId}
      targetControl={targetControl}
    >
      {children}
      {phase === "picker" && <DemoWelcomeModal />}
      {phase === "run" && <GuideRail />}
      {showChecklist && phase !== "run" && <DemoChecklistLauncher />}
      {import.meta.env.DEV && (
        <div
          className="hidden"
          data-demo-guide-phase={phase}
          data-demo-guide-target={targetWidgetId ?? ""}
        />
      )}
    </WidgetSpotlightProvider>
  );
}
